import { c as _c } from "react/compiler-runtime";
import { toApplicationData } from '@data/api/mappers/workflowData/toApplicationData';
import { toApplicationReviewData } from '@data/api/mappers/workflowData/toApplicationReviewData';
import { toBasicRegistrationData } from '@data/api/mappers/workflowData/toBasicRegistrationData';
import { toConfirmEmailData } from '@data/api/mappers/workflowData/toConfirmEmailData';
import { toEventWallData } from '@data/api/mappers/workflowData/toEventWallData';
import { toInterestsData } from '@data/api/mappers/workflowData/toInterestsData';
import { toLocalCommunityData } from '@data/api/mappers/workflowData/toLocalCommunityData';
import { toMembershipData } from '@data/api/mappers/workflowData/toMembershipData';
import { toPhotoData } from '@data/api/mappers/workflowData/toPhotoData';
import { PhoneNumberWorkflowData } from '@data/api/mappers/workflowData/toPhoneNumberData';
import { requester, RequestInfo } from '@data/api/requester';
import { QueryOptions } from '@data/queries/types';
import { toPhoneNumberData } from '@data/schemas/phoneNumberData';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { toCreatorOfferData } from '@data/api/mappers/workflowData/toCreatorOfferData';
export type WorkflowStep = 'basic' | 'photo' | 'interests' | 'membership' | 'localcommunity' | 'application-review' | 'application' | 'basic-registration' | 'confirm-email' | 'creator-offer' | 'event-wall' | 'event-wall-detail-page' | 'phone-number';
export type Data = TBasicStepWorkflowData | TConfirmEmailWorkflowData | ApplicationReviewWorkflowData | PhotoWorkflowData | InterestsWorkflowData | MembershipWorkflowData | TLocalcommunityWorkflowData | TApplicationWorkflowData | CreatorOfferWorkflowData | EventWallWorkflowData | PhoneNumberWorkflowData;

/** BE route: registration_api_photo_index */
const photoRequest: RequestInfo<PhotoWorkflowData> = {
  method: 'GET',
  url: '/registration/photo',
  responseMapper: toPhotoData
};

/** BE route: registration_api_interest_index */
const interestsRequest: RequestInfo<InterestsWorkflowData> = {
  method: 'GET',
  url: '/registration/interests',
  responseMapper: toInterestsData
};

/** BE route: registration_api_membership_index */
const membershipRequest: RequestInfo<MembershipWorkflowData> = {
  method: 'GET',
  url: '/registration/membership',
  responseMapper: toMembershipData
};

/** BE route: registration_api_membership_creator_offer_index */
const creatorOfferRequest: RequestInfo<CreatorOfferWorkflowData> = {
  method: 'GET',
  url: '/registration/creator-offer',
  responseMapper: toCreatorOfferData
};

/** BE route: registration_api_event_wall_index */
const eventWallRequest: RequestInfo<EventWallWorkflowData> = {
  method: 'GET',
  url: '/registration/event-wall',
  responseMapper: toEventWallData
};

/** BE route: registration_api_localcommunity_index */
const localCommunityRequest: RequestInfo<TLocalcommunityWorkflowData> = {
  method: 'GET',
  url: '/registration/local-community',
  responseMapper: toLocalCommunityData
};

/** BE route: registration_api_application_index */
const applicationRequest: RequestInfo<TApplicationWorkflowData> = {
  method: 'GET',
  url: '/registration/application',
  responseMapper: toApplicationData
};

/** BE route: registration_api_approval_pending_patch */
const applicationReviewRequest: RequestInfo<ApplicationReviewWorkflowData> = {
  method: 'PATCH',
  url: '/registration/application-review',
  responseMapper: toApplicationReviewData
};

/** BE route: registration_api_basic_step_index */
const basicRegistrationRequest: RequestInfo<TBasicStepWorkflowData> = {
  method: 'GET',
  url: '/registration/basic',
  responseMapper: toBasicRegistrationData,
  params: typeof window !== 'undefined' ? Object.fromEntries(new URLSearchParams(window.location.search).entries()) : undefined
};

/** BE route: registration_api_confirmation_step_index */
const confirmEmailRequest: RequestInfo<TConfirmEmailWorkflowData> = {
  method: 'GET',
  url: '/registration/confirm-email',
  responseMapper: toConfirmEmailData
};

/** BE route: registration_api_phone_number_index */
const phoneNumberRequest: RequestInfo<PhoneNumberWorkflowData> = {
  method: 'GET',
  url: '/registration/phone-number',
  responseMapper: toPhoneNumberData
};
const STEP_TO_REQUEST: Record<WorkflowStep, () => Promise<Data> | undefined> = {
  photo: () => requester(photoRequest),
  interests: () => requester(interestsRequest),
  localcommunity: () => requester(localCommunityRequest),
  'application-review': () => requester(applicationReviewRequest),
  application: () => requester(applicationRequest),
  basic: () => requester(basicRegistrationRequest),
  membership: () => requester(membershipRequest),
  'confirm-email': () => requester(confirmEmailRequest),
  'creator-offer': () => requester(creatorOfferRequest),
  'basic-registration': () => undefined,
  'event-wall': () => requester(eventWallRequest),
  'event-wall-detail-page': () => requester(eventWallRequest),
  'phone-number': () => requester(phoneNumberRequest)
};

/** Steps sharing an underlying request should also share a query cache key. */
const QUERY_KEY_STEP: Partial<Record<WorkflowStep, WorkflowStep>> = {
  'event-wall-detail-page': 'event-wall'
};
export type RegistrationWorkflowNavigatorOptions = QueryOptions<Data, ApiError>;
export const useRegistrationWorkflowNavigator = (step, options) => {
  const $ = _c(8);
  const t0 = QUERY_KEY_STEP[step] ?? step;
  let t1;
  if ($[0] !== t0) {
    t1 = ["registration:workflow", {
      step: t0
    }];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryKey = t1;
  let t2;
  if ($[2] !== step) {
    t2 = () => STEP_TO_REQUEST[step]() ?? requester(membershipRequest);
    $[2] = step;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const queryFn = t2;
  let t3;
  if ($[4] !== options || $[5] !== queryFn || $[6] !== queryKey) {
    t3 = {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      ...options,
      queryKey,
      queryFn
    };
    $[4] = options;
    $[5] = queryFn;
    $[6] = queryKey;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};